import React from 'react';
import { Band, PullQuote } from './Band';
import { gateFacts, modelIndependence, pipelineDetail } from '../../data/site';

/** The full production and model-independence argument. Methodology page only. */
export function ProductionDetail() {
  return <Band dark id="production" eyebrow="Scenario production" title="How a scenario is generated, attacked and released." description="A trap only counts if a frontier model walks past it, and a clean case only counts if it stays clean. Each project is generated, constructed, then attacked by the current models." aside={<PullQuote dark data-id="element-283" data-mp="1khlhhnpcqa5x">
          What survives goes live. What stops surviving is pulled, and
          regenerated.
        </PullQuote>} data-id="element-282" data-mp="10sjdhy9zsohf">
      <ol className="grid gap-4 lg:grid-cols-4" data-id="element-284" data-mp="1flwf7cly6f5x">
        {pipelineDetail.map(stage => <li key={stage.stage} className="flex" data-id="element-285" data-mp="1tgyrchhe10r6">
            <div className="flex w-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-id="element-286" data-mp="1ol9p26dzh5r6">
              <div className="flex items-center justify-between" data-id="element-287" data-mp="25p89zfu3nfjx">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-teal-light" data-id="element-288" data-mp="2amrq6lacy2ei">
                  {stage.stage}
                </span>
                <span className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.1em] text-bone/50" data-id="element-289" data-mp="2fkt5cv4nxhkp">
                  {stage.engine}
                </span>
              </div>
              <h3 className="mt-5 font-display text-xl font-medium text-bone" data-id="element-290" data-mp="2okyd56drqhze">
                {stage.title}
              </h3>
              <p className="mt-2.5 max-w-[38ch] text-[15px] leading-relaxed text-bone/60" data-id="element-291" data-mp="2yc2fn6m9cadc">
                {stage.body}
              </p>
            </div>
          </li>)}
      </ol>

      <div id="model-independence" className="mt-20 scroll-mt-24 border-t border-white/10 pt-16" data-id="element-292" data-mp="38b1qmvuhwi3i">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-teal-light" data-id="element-293" data-mp="3cc3szljpla9p">
          Model independence
        </p>
        <h3 className="mt-5 max-w-3xl font-display text-[30px] font-light leading-[1.1] tracking-tight text-bone sm:text-[40px]" data-id="element-294" data-mp="1d6tvfgxydsq8">
          We're long model capability, not short it.
        </h3>

        <div className="mt-9 grid gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-295" data-mp="1m06xb8dzw4og">
          <p className="max-w-[68ch] text-lg leading-relaxed text-bone/70" data-id="element-296" data-mp="1h98i6c4qtcms">
            The assistant inside the assessment is an instrument the candidate
            operates, not something Lentic resells. Swap it for any frontier
            model and the product works identically. There is no prompt library
            that constitutes the intellectual property and no fine-tune that
            constitutes the moat.
          </p>
          <PullQuote dark data-id="element-297" data-mp="20y96pz7pjkdm">
            As assistants get more fluent, more work is delegated, more output
            is plausible, and a confident wrong answer accepted without checking
            becomes more common and more expensive. The question of who stays
            accountable gets more urgent, not less.
          </PullQuote>
        </div>

        <dl className="mt-14 grid gap-x-10 gap-y-8 border-t border-white/10 pt-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-298" data-mp="3sn06yhho0owr">
          {modelIndependence.map(item => <div key={item.label} data-id="element-299" data-mp="3iub76jvh8zh9">
              <dt className="font-mono text-xs uppercase tracking-[0.14em] text-teal-light" data-id="element-300" data-mp="3it00ab6qjblx">
                {item.label}
              </dt>
              <dd className="mt-3 max-w-[18ch] font-display text-2xl font-light leading-tight text-bone" data-id="element-301" data-mp="38vlmykg3z2xu">
                {item.value}
              </dd>
            </div>)}
        </dl>
      </div>

      <dl className="mt-20 grid gap-x-10 gap-y-8 border-t border-white/10 pt-10 sm:grid-cols-3" data-id="element-302" data-mp="2zx056rignry4">
        {gateFacts.map(fact => <div key={fact.label} data-id="element-303" data-mp="34x5tpytfoybz">
            <dt className="font-display text-3xl font-light text-bone" data-id="element-304" data-mp="y4vax9dhoik8">
              {fact.value}
            </dt>
            <dd className="mt-2 max-w-[30ch] text-[15px] leading-snug text-bone/60" data-id="element-305" data-mp="133yfkspk4mro">
              {fact.label}
            </dd>
          </div>)}
      </dl>

      <p className="mt-16 max-w-[80ch] text-sm leading-relaxed text-bone/50" data-id="element-306" data-mp="2u7qilj65dnx0">
        Claim discipline: we don't imply a security certification, publish
        model-defeat rates we can't reproduce, or describe the product as
        detecting AI use. The claim is narrow. Our scenarios are re-tested
        against current frontier models and deprecated when they stop working.
      </p>
    </Band>;
}